/**
 * @file useCrudMutations.ts
 * @description Shared CRUD mutation hooks to reduce duplication across pages
 * @module hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

interface CrudMutationOptions {
  /** Query keys to invalidate on success */
  queryKeys: string[];
  /** Entity name for notification messages (e.g., 'Customer', 'Product') */
  entityName: string;
  /** Callback on successful mutation */
  onSuccessCallback?: () => void;
  /** Callback on error */
  onErrorCallback?: (error: Error) => void;
}

/**
 * Generic create mutation hook
 */
export function useCreateMutation<TData, TInput>(
  mutationFn: (input: TInput) => Promise<TData>,
  options: CrudMutationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      options.queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      notifications.show({
        title: 'Success',
        message: `${options.entityName} created successfully`,
        color: 'green',
      });
      options.onSuccessCallback?.();
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
      options.onErrorCallback?.(error);
    },
  });
}

/**
 * Generic update mutation hook
 */
export function useUpdateMutation<TData, TInput>(
  mutationFn: (params: { id: string; data: TInput }) => Promise<TData>,
  options: CrudMutationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      options.queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      notifications.show({
        title: 'Success',
        message: `${options.entityName} updated successfully`,
        color: 'green',
      });
      options.onSuccessCallback?.();
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
      options.onErrorCallback?.(error);
    },
  });
}

/**
 * Generic delete mutation hook
 */
export function useDeleteMutation<TData = void>(
  mutationFn: (id: string) => Promise<TData>,
  options: CrudMutationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      options.queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      notifications.show({
        title: 'Success',
        message: `${options.entityName} deleted successfully`,
        color: 'green',
      });
      options.onSuccessCallback?.();
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
      options.onErrorCallback?.(error);
    },
  });
}

/**
 * Generic action mutation hook (for suspend, reactivate, revoke, etc.)
 */
export function useActionMutation<TData, TInput = void>(
  mutationFn: (input: TInput) => Promise<TData>,
  options: {
    queryKeys: string[];
    successMessage: string;
    successColor?: string;
    onSuccessCallback?: () => void;
    onErrorCallback?: (error: Error) => void;
  }
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      options.queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      notifications.show({
        title: 'Success',
        message: options.successMessage,
        color: options.successColor || 'green',
      });
      options.onSuccessCallback?.();
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
      options.onErrorCallback?.(error);
    },
  });
}

/**
 * Show validation error notification
 */
export function showValidationError(message: string) {
  notifications.show({
    title: 'Validation Error',
    message,
    color: 'red',
  });
}
